import { useCallback, useRef, useState, useMemo } from "react";
import {
  IonModal,
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import { motion, AnimatePresence } from "framer-motion";
import closeIcon from "../../assets/imgs/close.svg";
import { useSelector } from "react-redux";
import { actions } from "../../Redux/store";
import RankingTableTabs from "../Commons/RankingTableTabs/RankingTableTabs";
import TradeAnalyzerPlayersCard from "./Componet/TradeAnalyzerPlayersCard";
import { useRanking2025Season } from "../../Controllers/PlayerRankingApi";
import { hapticsImpactLight } from "../../lib/utils";
import { SelectLeagueButton } from "../LeagueSync/SelectLeagueButton/SelectLeagueButton";

// Define Variants type inline since it's no longer exported
type Variants = {
  [key: string]: {
    [key: string]: any;
    transition?: any;
  };
};

const continueButtonVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 50,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
      delay: 0.3,
    },
  },
  exit: {
    opacity: 0,
    y: 50,
    scale: 0.9,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
    },
  },
  hover: {
    scale: 1.02,
    boxShadow: "0 8px 25px rgba(33, 184, 205, 0.3)",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 15,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
    },
  },
};

// Skeleton loader component
const PlayerCardSkeleton = ({ index }: { index: number }) => (
  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
    <div className="relative">
      <div className="w-[44px] h-[44px] bg-gray-600/50 rounded-xl" />
      <div className="absolute bottom-[-1px] right-[-1px] w-[23px] h-[23px] bg-gray-500/50 rounded-full" />
    </div>
    <div className="flex-1">
      <div className="h-[14px] bg-gray-600/50 rounded w-3/4 mb-2" />
      <div className="h-[10px] bg-gray-500/50 rounded w-1/2" />
    </div>
    <div className="w-[16px] h-[16px] bg-gray-500/50 rounded" />
  </div>
);

function PlayerSearchModal() {
  const modal = useRef<HTMLIonModalElement>(null);
  const isOpen = useSelector((sate: any) => sate.modal.playerSearchModal?.open);
  const playerSectionType = useSelector(
    (sate: any) => sate.modal.playerSearchModal?.data?.type,
  );

  // Get trading players from Redux
  const tradingAwayPlayers = useSelector(
    (state: any) => state.tradingPlayers.tradingAwayPlayers,
  );
  const tradingForPlayers = useSelector(
    (state: any) => state.tradingPlayers.tradingForPlayers,
  );

  const toggle = () => {
    actions.modal.closePlayerSearchModal(null);
  };

  function dismiss() {
    modal.current?.dismiss();
    toggle();
  }

  type PositionItem = {
    id: number;
    name: string;
  };

  const positions = [
    { id: 1, name: "QB" },
    { id: 2, name: "RB" },
    { id: 3, name: "WR" },
    { id: 4, name: "TE" },
  ];

  const defaultPosition = { id: 1, name: "QB" };

  const [positionSelected, setPosicionSelected] =
    useState<PositionItem>(defaultPosition);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const handleSelectPosition = useCallback((item: PositionItem) => {
    setPosicionSelected(item);
  }, []);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    [],
  );

  const handlePlayerClick = useCallback(
    (player: any) => {
      // Check if player is already selected and unselect if they are
      const isInTradingAway = tradingAwayPlayers.some(
        (selectedPlayer: any) => selectedPlayer.id === player.id,
      );
      const isInTradingFor = tradingForPlayers.some(
        (selectedPlayer: any) => selectedPlayer.id === player.id,
      );

      // If player is already selected, remove them
      if (isInTradingAway) {
        hapticsImpactLight();
        actions.tradingPlayers.removeTradingAwayPlayer(player.id);
        return;
      }

      if (isInTradingFor) {
        hapticsImpactLight();
        actions.tradingPlayers.removeTradingForPlayer(player.id);
        return;
      }

      // If player is not selected, add them based on section type
      if (playerSectionType === "tradingAway") {
        // Check if tradingAwayPlayers already has 3 players
        if (tradingAwayPlayers.length >= 3) {
          return; // Stop processing if limit reached
        }
        hapticsImpactLight();
        actions.tradingPlayers.addTradingAwayPlayer(player);

        // Check if this was the 3rd player and dismiss modal
        if (tradingAwayPlayers.length + 1 >= 3) {
          dismiss();
        }
      } else if (playerSectionType === "tradingFor") {
        // Check if tradingForPlayers already has 3 players
        if (tradingForPlayers.length >= 3) {
          return; // Stop processing if limit reached
        }
        hapticsImpactLight();
        actions.tradingPlayers.addTradingForPlayer(player);

        // Check if this was the 3rd player and dismiss modal
        if (tradingForPlayers.length + 1 >= 3) {
          dismiss();
        }
      }
    },
    [playerSectionType, tradingAwayPlayers, tradingForPlayers],
  );

  const ranking2025Season = useRanking2025Season({ enabled: isOpen });

  // Helper function to check if a player is already selected
  const isPlayerSelected = useCallback(
    (player: any) => {
      const isInTradingAway = tradingAwayPlayers.some(
        (selectedPlayer: any) => selectedPlayer.id === player.id,
      );
      const isInTradingFor = tradingForPlayers.some(
        (selectedPlayer: any) => selectedPlayer.id === player.id,
      );
      return isInTradingAway || isInTradingFor;
    },
    [tradingAwayPlayers, tradingForPlayers],
  );

  // Check if at least one player is selected to show Continue button based on section type
  const hasSelectedPlayers = useMemo(() => {
    if (playerSectionType === "tradingAway") {
      return tradingAwayPlayers.length > 0;
    } else if (playerSectionType === "tradingFor") {
      return tradingForPlayers.length > 0;
    }
    return false;
  }, [playerSectionType, tradingAwayPlayers, tradingForPlayers]);

  // Filter players based on selected position and search query (keep selected players visible but highlighted)
  const filteredPlayers = useMemo(() => {
    const players = ranking2025Season?.data?.data || [];

    return players.filter((player: any) => {
      // If searching, search across all positions; otherwise filter by selected position
      const matchesPosition = searchQuery.trim()
        ? true // When searching, include all positions
        : player.playerPosition === positionSelected.name;

      // Filter by search query (if provided)
      const matchesSearch =
        searchQuery.trim() === "" ||
        player.playerName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase().trim());

      return matchesPosition && matchesSearch;
    });
  }, [ranking2025Season?.data?.data, positionSelected.name, searchQuery]);

  const continueButton = () => {
    hapticsImpactLight();
    dismiss();
  };

  const isLoading = ranking2025Season?.isLoading;
  const hasData = ranking2025Season?.data?.data && ranking2025Season.data.data.length > 0;

  return (
    <IonModal
      onDidDismiss={dismiss}
      className={`full-modal`}
      isOpen={isOpen}
      backdropDismiss={false}
      ref={modal}
    >
      <>
        <IonHeader class="ion-no-border modal-ion-header">
          <IonToolbar className="custom-toolbar mt-[10px]">
            <div className="p-2 px-3 flex justify-between items-center sticky top-0 z-50">
              <div
                onClick={dismiss}
                className="flex items-center justify-center w-[32px] h-[32px] bg-white/10 rounded-full cursor-pointer"
              >
                <img
                  src={closeIcon}
                  alt="close"
                  className="w-[12.4px] h-[12.4px]"
                />
              </div>
              <IonTitle>
                <div className="flex flex-row items-center justify-center">
                  <span className=" [@media(min-width:375px)]:text-[22px] text-[20px] font-medium text-white">
                    Player Search
                  </span>
                </div>
              </IonTitle>
            </div>
            {/* <div className="flex items-center justify-center">
              <SelectLeagueButton />
            </div> */}
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="pb-6">
            <div className="mt-6 px-[20px]">
              <div className="relative flex items-center flex-1">
                <span className="absolute left-2 text-white/70 z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-[9.74px] w-[9.74px]"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.9 14.32a8 8 0 111.42-1.42l4.31 4.3a1 1 0 01-1.42 1.42l-4.3-4.31zM8 14a6 6 0 100-12 6 6 0 000 12z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search player name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="text-white h-[36px] placeholder:text-[10px] placeholder:text-white/70 text-[12px] pl-6 py-2 rounded-[10px] outline-none focus:outline-none bg-white/10 w-full"
                />
              </div>

              {/* Show position tabs only when not searching */}
              {!searchQuery.trim() ? (
                <div className="mt-4 overflow-x-auto no-scrollbar">
                  <RankingTableTabs
                    tabItems={positions}
                    tabCustomStyle="w-[50px] h-[28px] shrink-0 text-[12px] rounded-[8px]"
                    onTabSelect={handleSelectPosition}
                    InicialselectedTab={positionSelected}
                    positionTab={true}
                  />
                </div>
              ) : (
                /* Show search results title when searching */
                <div className="mt-6">
                  <h3 className="text-white text-[16px] font-medium">
                    Search Results
                    {filteredPlayers.length > 0 && (
                      <span className="ml-2 text-[#21B8CD] text-[14px]">
                        ({filteredPlayers.length} found)
                      </span>
                    )}
                  </h3>
                </div>
              )}
            </div>

            <div className="mt-5">
              {isLoading ? (
                // Skeleton loading state
                <div className="grid grid-cols-1 gap-[3px] mt-3 px-[20px]">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <PlayerCardSkeleton key={index} index={index} />
                  ))}
                </div>
              ) : (
                // Actual player data
                <div className="grid grid-cols-1 gap-[3px] mt-3">
                  {filteredPlayers.length > 0 ? (
                    filteredPlayers.map((player: any, index: number) => (
                      <div key={`${player.playerName}-${index}`}>
                        <TradeAnalyzerPlayersCard
                          playerSmallIcon={player.playerHeadshotURL}
                          playerName={player.playerName}
                          playerPosition={player.playerPosition}
                          playerStatus={player.playerTeam}
                          bgColor={player.playerTeamShort}
                          cardBg={false}
                          isSelected={isPlayerSelected(player)}
                          onClick={() => handlePlayerClick(player)}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div>
                        <p className="text-white/70 text-sm">
                          {searchQuery.trim()
                            ? `No players found matching "${searchQuery}"`
                            : `No ${positionSelected.name} players found`}
                        </p>
                      </div>
                      <p className="text-white/50 text-xs mt-2">
                        Try adjusting your search or selecting a different position
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <AnimatePresence>
            {hasSelectedPlayers && (
              <motion.div
                className="fixed bottom-0 left-0 right-0 z-50"
                style={{
                  paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
                }}
                variants={continueButtonVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="px-[45px]">
                  <motion.button
                    className="h-[48px] bg-[#21B8CD] w-full flex justify-center items-center rounded-[50.13px]"
                    variants={continueButtonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    onClick={continueButton}
                  >
                    <motion.span 
                      className="text-black text-[12.03px] font-medium"
                      animate={{
                        scale: [1, 1.05, 1],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      Continue
                    </motion.span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </IonContent>
      </>
    </IonModal>
  );
}

export default PlayerSearchModal;
